import { useEffect, useState } from "react";
import { ArrowRight, CheckCheck, SkipForward, AlertTriangle, Loader2, ClipboardCheck, ChevronDown, ChevronRight, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const STATE_STYLE = {
  done: "bg-green-100 text-green-700",
  current: "bg-brand text-white",
  pending: "bg-slate-100 text-slate-500",
  skipped: "bg-amber-100 text-amber-700",
  blocked: "bg-red-100 text-red-700",
  not_applicable: "bg-slate-50 text-slate-400",
};

export function JobWorkflow({ jobId, status, onRefresh, actions, refreshKey }) {
  const [wf, setWf] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [skipFor, setSkipFor] = useState(null);
  const [inspectOpen, setInspectOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => { try { setWf(await api(`/jobs/${jobId}/workflow`)); } catch { /* ignore */ } };
  useEffect(() => { load(); }, [jobId, status, refreshKey]);

  if (!wf) return null;
  const refresh = () => { load(); onRefresh?.(); };

  const run = async (fn) => { setBusy(true); try { await fn(); refresh(); } catch (e) { toast.error(e.body?.detail?.message || e.message); } finally { setBusy(false); } };

  const trigger = (key) => {
    const a = actions || {};
    switch (key) {
      case "check_in": return a.checkIn?.();
      case "awaiting_parts": return a.startWork?.();
      case "in_progress": return a.startWork?.();
      case "quality_check": return setInspectOpen(true);
      case "work_completed": return a.markCompleted?.();
      case "customer_notified": return run(() => api(`/jobs/${jobId}/notify-customer`, { method: "POST" }).then(() => toast.success("Customer notified")));
      case "invoice_issued": return a.openInvoice?.();
      case "payment": return a.openPayment?.();
      case "ready_for_collection": return run(() => api(`/jobs/${jobId}/ready-for-collection`, { method: "POST" }).then(() => toast.success("Marked ready for collection")));
      case "collected": return a.gotoHandover?.();
      case "closed": return run(() => api(`/jobs/${jobId}/close`, { method: "POST", body: JSON.stringify({}) }).then(() => toast.success("Job closed")));
      default: return null;
    }
  };

  const nextStep = wf.steps.find((s) => s.key === wf.next);
  const allDone = !wf.next;

  return (
    <div className="mb-4" data-testid="job-workflow">
      {wf.pending_approval ? (
        <div data-testid="next-action-awaiting" className="flex w-full items-center gap-3 rounded-xl border border-warning/40 bg-warning-bg px-5 py-4 text-warning">
          <AlertTriangle className="h-5 w-5" /><span className="font-display text-lg font-extrabold uppercase">Awaiting Customer Approval</span>
        </div>
      ) : nextStep ? (
        <div className="flex items-stretch gap-2">
          <button onClick={() => trigger(nextStep.key)} disabled={busy || nextStep.state === "blocked"} data-testid="job-next-step"
            className={`flex flex-1 items-center justify-between gap-3 rounded-xl px-5 py-4 text-left transition-all ${nextStep.state === "blocked" ? "cursor-not-allowed bg-red-50 text-red-700" : "bg-brand text-white hover:scale-[1.005]"}`}>
            <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="text-[11px] font-bold uppercase tracking-widest opacity-80">Next step</span>
              <span className="font-display text-lg font-extrabold uppercase">{nextStep.label}</span>
            </span>
            {nextStep.state === "blocked" ? <AlertTriangle className="h-5 w-5 shrink-0" /> : (busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowRight className="h-5 w-5 shrink-0" />)}
          </button>
          {nextStep.skippable && (
            <button onClick={() => setSkipFor(nextStep)} data-testid="job-skip-step" title="Skip / Override"
              className="flex items-center gap-1.5 rounded-xl border border-amber-300 bg-amber-50 px-3 text-sm font-bold text-amber-700 hover:bg-amber-100">
              <SkipForward className="h-4 w-4" />Skip
            </button>
          )}
        </div>
      ) : (
        <div data-testid="next-action-done" className="flex w-full items-center gap-3 rounded-xl border border-success/40 bg-success/10 px-5 py-4 text-success">
          <CheckCheck className="h-5 w-5" /><span className="font-display text-lg font-extrabold uppercase">Job Complete</span>
        </div>
      )}
      {nextStep?.state === "blocked" && (
        <p className="mt-1 text-xs font-semibold text-red-600" data-testid="job-step-blocked">{nextStep.label} is blocked: a mandatory final inspection must be completed first.</p>
      )}

      {wf.outstanding_skips.length > 0 && (
        <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 p-3" data-testid="job-outstanding-skips">
          <p className="mb-1 flex items-center gap-2 text-xs font-extrabold uppercase tracking-wide text-amber-800"><AlertTriangle className="h-4 w-4" />Skipped steps — follow-up needed</p>
          {wf.outstanding_skips.map((s) => (
            <div key={s.step} className="flex items-center justify-between gap-2 py-1 text-sm">
              <span className="min-w-0"><b>{s.label}</b> — {s.reason} <span className="text-amber-700/70">· {s.by}</span></span>
              <button onClick={() => run(() => api(`/jobs/${jobId}/complete-skipped-step`, { method: "POST", body: JSON.stringify({ step: s.step }) }))} data-testid={`complete-skip-${s.step}`}
                className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-amber-400 bg-white px-2 py-1 text-xs font-bold text-amber-800 hover:bg-amber-100"><RotateCcw className="h-3 w-3" />Complete now</button>
            </div>
          ))}
        </div>
      )}

      <button onClick={() => setExpanded(!expanded)} data-testid="job-workflow-toggle" className="mt-2 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-muted-foreground hover:text-midnight">
        {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}Workflow progress
      </button>
      {expanded && (
        <ol className="mt-2 grid gap-1.5 rounded-xl border border-border bg-card p-3 sm:grid-cols-2" data-testid="job-workflow-steps">
          {wf.steps.map((s) => (
            <li key={s.key} className="flex items-center justify-between gap-2 text-sm" data-testid={`wf-step-${s.key}`}>
              <span className={s.state === "not_applicable" ? "text-slate-400" : "text-midnight"}>{s.label}</span>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${STATE_STYLE[s.state]}`} data-testid={`wf-state-${s.key}`}>
                {s.state === "not_applicable" ? "N/A" : s.state === "skipped" ? "Skipped" : s.state}
              </span>
            </li>
          ))}
        </ol>
      )}

      <SkipStepDialog jobId={jobId} step={skipFor} onClose={() => setSkipFor(null)} onDone={refresh} />
      <InspectionDialog jobId={jobId} open={inspectOpen} onClose={() => setInspectOpen(false)} onDone={refresh} />
    </div>
  );
}

function SkipStepDialog({ jobId, step, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setReason(""); }, [step]);
  if (!step) return null;
  const submit = async () => {
    if (!reason.trim()) { toast.error("Enter a reason"); return; }
    setSaving(true);
    try {
      await api(`/jobs/${jobId}/skip-step`, { method: "POST", body: JSON.stringify({ step: step.key, reason }) });
      toast.success(`${step.label} skipped by override`);
      onDone(); onClose();
    } catch (e) { toast.error(e.body?.detail?.message || e.message); } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!step} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="skip-step-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><SkipForward className="h-5 w-5 text-amber-600" />Skip / Override: {step.label}</DialogTitle>
          <DialogDescription>This step will be recorded as <b>Skipped by Override</b> — not completed.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-900">
            <p><b>Why this step matters:</b> {step.why}</p>
            {step.consequence && <p className="mt-1"><b>Consequence of skipping:</b> {step.consequence}</p>}
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold uppercase text-muted-foreground">Reason for override *</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} data-testid="skip-reason"
              className="w-full rounded-lg border border-input bg-card px-3 py-2 outline-none focus:border-brand" placeholder="Explain why this step is being skipped" />
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="rounded-lg border border-border bg-card px-4 py-2.5 text-sm font-bold text-midnight hover:bg-secondary">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="skip-confirm" className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2.5 text-sm font-bold text-white disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <SkipForward className="h-4 w-4" />}Confirm override
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InspectionDialog({ jobId, open, onClose, onDone }) {
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setNotes("");
    api("/garage/settings").then((g) => {
      const cfg = (g.inspection_checklist?.items || []).filter(Boolean);
      setItems((cfg.length ? cfg : ["Road test completed", "Torque / fluids checked", "Warning lights cleared"]).map((label) => ({ label, pass: true, note: "" })));
    }).catch(() => setItems([{ label: "Final inspection completed", pass: true, note: "" }]));
  }, [open]);
  const submit = async () => {
    setSaving(true);
    try {
      await api(`/jobs/${jobId}/inspection`, { method: "POST", body: JSON.stringify({ items, notes }) });
      toast.success("Inspection recorded");
      onDone(); onClose();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="inspection-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ClipboardCheck className="h-5 w-5 text-brand" />Final Inspection</DialogTitle>
          <DialogDescription>Mark each item pass or fail before the vehicle is ready for collection.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i} className="flex items-center justify-between gap-2 rounded-lg border border-border p-2.5" data-testid={`inspect-item-${i}`}>
              <span className="text-sm text-midnight">{it.label}</span>
              <div className="flex gap-1">
                <button onClick={() => setItems((a) => a.map((x, j) => j === i ? { ...x, pass: true } : x))} data-testid={`inspect-pass-${i}`} className={`rounded-lg px-3 py-1.5 text-xs font-bold ${it.pass ? "bg-green-600 text-white" : "bg-slate-100 text-slate-500"}`}>Pass</button>
                <button onClick={() => setItems((a) => a.map((x, j) => j === i ? { ...x, pass: false } : x))} data-testid={`inspect-fail-${i}`} className={`rounded-lg px-3 py-1.5 text-xs font-bold ${!it.pass ? "bg-red-600 text-white" : "bg-slate-100 text-slate-500"}`}>Fail</button>
              </div>
            </div>
          ))}
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="inspect-notes" placeholder="Inspection notes (optional)" className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm outline-none focus:border-brand" />
        </div>
        <DialogFooter>
          <button onClick={submit} disabled={saving} data-testid="inspection-submit" className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-brand py-3 text-sm font-extrabold text-white disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardCheck className="h-4 w-4" />}Save inspection
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
